import {FC, useEffect, useState} from "react";
import {Role, User, UserPatch} from "../../../../types/domain.ts";
import {App, Form, FormProps, Modal, Segmented, Select} from "antd";
import {ROLE_OPTIONS} from "../../../../constants/constants.ts";
import {useFirs} from "../../../../queries/useFirs.ts";
import {useRegions} from "../../../../queries/useRegions.ts";
import {usersUserIdPatch} from "../../../../api/users/usersUserIdPatch.ts";
import {isFirManager, isRegionManager} from "../../../../utils/userTypeGuards.ts";

type Props = {
    user?: User;
    onClose: VoidFunction;
    onEdit: VoidFunction;
}

type FormType = {
    role: Role;
    fir_ids?: string[];
    region_id?: number;
}

const makePatchBody = (values: FormType): UserPatch => {
    switch(values.role) {
        case 'fir_manager':
        case 'fir_reader':
            if(!values.fir_ids?.length) throw new Error();

            return {
                role: values.role,
                fir_ids: values.fir_ids,
            };
        case 'region_manager':
            if(!values.region_id) throw new Error();

            return {
                role: values.role,
                region_id: values.region_id,
            };
        case 'staff':
            return {
                role: values.role,
            };
    }
}

export const EditUserModal: FC<Props> = ({ user, onClose: _onClose, onEdit }) => {
    const { message } = App.useApp();
    const [form] = Form.useForm<FormType>();
    const [loading, setLoading] = useState(false);

    const role = Form.useWatch('role', form);

    const { data: firs } = useFirs();
    const { data: regions } = useRegions();

    const onClose = () => {
        form.resetFields();
        _onClose();
    }

    useEffect(() => {
        if(user) {
            form.setFieldsValue({
                role: user.role,
                fir_ids: isFirManager(user) ? user.firs.map(({ id }) => id) : undefined,
                region_id: isRegionManager(user) ? user.region.id : undefined
            })
        }
    }, [user]);

    const handleFinish: FormProps<FormType>['onFinish'] = async (values) => {
        try {
            setLoading(true);
            const body = makePatchBody(values);
            await usersUserIdPatch(user!.id, body);
            void message.success('Сохранено');
            onEdit();
            onClose();
        } catch(e) {
            void message.error(`Произошла ошибка при редактировании пользователя (${JSON.stringify(e)})`);
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal
            open={Boolean(user)}
            title={`Редактировать ${user?.first_name} ${user?.last_name}`}
            onOk={form.submit}
            okButtonProps={{ loading }}
            onCancel={onClose}
        >
            {user && (
                <Form<FormType>
                    form={form}
                    onFinish={handleFinish}
                    layout="vertical"
                >
                    <Form.Item<FormType>
                        name="role"
                        label="Роль"
                        rules={[{ type: 'enum', required: true, enum: ["fir_reader", "fir_manager", "region_manager", "staff"] }]}
                    >
                        <Segmented options={ROLE_OPTIONS} />
                    </Form.Item>
                    {(role === 'fir_manager' || role === 'fir_reader') && (
                        <Form.Item<FormType>
                            name="fir_ids"
                            label="РПИ"
                            rules={[{ type: 'array', required: true }]}
                        >
                            <Select
                                options={firs?.map(({ id, name }) => ({
                                    value: id,
                                    label: `${name} (${id})`
                                }))}
                                mode="multiple"
                                maxTagCount="responsive"
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    )}
                    {role === 'region_manager' && (
                        <Form.Item<FormType>
                            name="region_id"
                            label="Регион"
                            rules={[{ type: 'number', required: true }]}
                        >
                            <Select
                                options={regions?.map(({ id, name }) => ({
                                    value: id,
                                    label: name
                                }))}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    )}
                </Form>
            )}
        </Modal>
    )
}